// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

// Local IndexedDB store for offline support (#22): an outbox of writes made
// while offline, plus a read-through cache so the add/edit transaction
// form's dropdowns still work offline. Dexie, not SQLite-Wasm/RxDB — see
// docs/design-notes.md §5 for why.

import Dexie from 'dexie';

export const db = new Dexie('idp-offline');

db.version(1).stores({
  // ++localId: Dexie auto-increment PK, local-only (never sent to the server).
  // status: 'pending' | 'failed'. op: 'create' | 'update' | 'delete'.
  // targetId: the server transaction id this write applies to, or null for
  // a still-unsynced 'create' (resolved via clientRef once that lands).
  outbox: '++localId, status, createdAt',
  cache_transactions: 'id',
  cache_categories: 'id',
  cache_payment_modes: 'id',
});

// ── Outbox ──────────────────────────────────────────────────────────
export function enqueueOutboxItem({
  entity,
  op,
  targetId,
  payload,
  clientRef,
  dependsOnClientRef,
}) {
  return db.outbox.add({
    entity,
    op,
    targetId: targetId ?? null,
    payload,
    clientRef: clientRef ?? null,
    // Set only for an update/delete queued against a 'create' that hasn't
    // synced yet (targetId is null until that create resolves to a real
    // server id) — see outbox.js's replay for how this gets resolved.
    dependsOnClientRef: dependsOnClientRef ?? null,
    status: 'pending',
    attempts: 0,
    error: null,
    createdAt: Date.now(),
  });
}

export function getPendingOutboxItems() {
  return db.outbox.where('status').equals('pending').sortBy('createdAt');
}

export function getFailedOutboxItems() {
  return db.outbox.where('status').equals('failed').sortBy('createdAt');
}

export function countPendingOutboxItems() {
  return db.outbox.where('status').equals('pending').count();
}

// ── Read-through caches ───────────────────────────────────────────────
export async function cacheTransactions(transactions) {
  if (!transactions?.length) return;
  await db.cache_transactions.bulkPut(
    transactions.map((t) => ({ ...t, id: t.id, cachedAt: Date.now() })),
  );
}

export function getCachedTransactions() {
  return db.cache_transactions.toArray();
}

export async function cacheCategories(categories) {
  if (!categories?.length) return;
  await db.cache_categories.bulkPut(
    categories.map((c) => ({ ...c, id: c.id, cachedAt: Date.now() })),
  );
}

export function getCachedCategories({ type } = {}) {
  if (!type) return db.cache_categories.toArray();
  return db.cache_categories.filter((c) => c.category_type === type).toArray();
}

export async function cachePaymentModes(modes) {
  if (!modes?.length) return;
  await db.cache_payment_modes.bulkPut(
    modes.map((m) => ({ ...m, id: m.id, cachedAt: Date.now() })),
  );
}

export function getCachedPaymentModes() {
  return db.cache_payment_modes.toArray();
}
